import { motion, AnimatePresence } from 'framer-motion'
import type { RadarBubble } from '@/services/api/teacher'
import { useAnimationConfig } from '../hooks/useReducedMotion'
import { RadarBubbleContent } from './RadarBubbleContent'
import { detectLocale, getTranslations } from '../i18n'
import { shouldFrost } from '../canvas/BubbleRenderer'
import '../styles/radar-tokens.css'

interface HoveredBubbleInfoProps {
  bubble: RadarBubble | null
  isAdminView?: boolean
}

/**
 * Hovered Bubble Info Panel
 *
 * Rendert bei Hover/Tap einer Bubble EXAKT denselben Inhalt wie der
 * FloatingTooltip (Radar-Cursor-Tooltip): beide nutzen RadarBubbleContent als
 * Single Source of Truth (gleiche Felder, gleiche Positionen, gleiche optionale
 * Badges), inkl. dunklem Glas-Styling. Damit sehen Radar-Tooltip und Panel unter
 * dem Radar identisch aus und können nicht drift-en.
 *
 * Platzierung: direkt unter dem Radar im Flex-Fluss. Um Layout-Sprüngen beim
 * Inhaltswechsel (1 vs. n Lektionen) und ein "Nach-unten-rutschen" zu vermeiden,
 * wird keine y-Translation animiert und eine Mindesthöhe reserviert.
 *
 * Phase 5.2: Floating UI - Hovered Bubble Info
 * frost-v2 (A2): sr-only-Zeile für Screenreader, wenn die Bubble am
 * Direktor-Gate hängt (das Canvas selbst hat keine per-Bubble-Aria-Anker).
 */
export function HoveredBubbleInfo({ bubble, isAdminView = false }: HoveredBubbleInfoProps) {
  const animConfig = useAnimationConfig()
  const t = getTranslations(detectLocale())

  return (
    <AnimatePresence>
      {bubble && (
        <motion.div
          className="rounded-xl p-3 border min-h-[72px]"
          style={{
            background: 'var(--glass-tooltip-bg)',
            backdropFilter: 'blur(var(--glass-tooltip-blur))',
            WebkitBackdropFilter: 'blur(var(--glass-tooltip-blur))',
            borderColor: 'var(--glass-tooltip-border)',
            boxShadow: 'var(--glass-tooltip-shadow)',
          }}
          // Keine y-Translation: verhindert, dass das Panel beim Einblenden
          // nach unten springt und evtl. aus dem Viewport rutscht.
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: animConfig.reducedMotion ? 0 : undefined,
          }}
        >
          {shouldFrost(bubble) && (
            <span className="sr-only">{t.aria.bubbleGate}</span>
          )}
          <RadarBubbleContent bubble={bubble} isAdminView={isAdminView} />
        </motion.div>
      )}
    </AnimatePresence>
  )
}
